/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3148Affiliation, U3148Organization, U3148Role } from "./U3148Service";

// Type for initLU (from LUState.initLU)
export type InitLU = U2000LUState["personalization"] & {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  currentOrganizationId: string;
};

// Definition of the U3148Context type
export interface U3148ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3148Organization>;
  gridOrgRoleStateHandlers: U2000GridStateHandlersReturn<U3148Role>;
  gridOrgAffiliationStateHandlers: U2000GridStateHandlersReturn<U3148Affiliation>;
  orgAffiliationCurrentTab: string | null;
  setOrgAffiliationCurrentTab: (tabId: string) => void;
  currentOrganizationId: string;
}

//  Props for the U3148ContextProvider component
interface U3148ContextProviderProps {
  initLU: InitLU;
  children: ReactNode;
}

// Creation of the U3148Context
const U3148Context = React.createContext<U3148ContextType | undefined>(undefined);

// U3148ContextProvider component to provide context values
export const U3148ContextProvider: React.FC<U3148ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({ luName: "u3148", launchSearch: initLU.launchSearch, hideSearch: initLU.hideSearch });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3148Organization>({ nbRecordPerPage: initLU.nbRecordPerPage ?? 50 });
  const U3148GridOrgRoleStateHandlers = useU2000GridStateHandlers<U3148Role>();
  const U3148GridOrgAffiliitonStateHandlers = useU2000GridStateHandlers<U3148Affiliation>();

  const [orgAffiliationCurrentTab, setOrgAffiliationCurrentTab] = React.useState<string | null>(null);
  const currentOrganizationId = initLU.currentOrganizationId ?? "";

  const contextValue = useMemo<U3148ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      gridOrgRoleStateHandlers: U3148GridOrgRoleStateHandlers,
      gridOrgAffiliationStateHandlers: U3148GridOrgAffiliitonStateHandlers,
      orgAffiliationCurrentTab,
      setOrgAffiliationCurrentTab,
      currentOrganizationId,
    }),
    [
      U2000GridStateHandlers,
      U2000SearchStateHandlers,
      U2000LUStateHandlers,
      U3148GridOrgRoleStateHandlers,
      U3148GridOrgAffiliitonStateHandlers,
      orgAffiliationCurrentTab,
      currentOrganizationId,
    ]
  );

  return <U3148Context.Provider value={contextValue}>{children}</U3148Context.Provider>;
};

// Custom hook to use the U3148Context
export function useU3148Context(): U3148ContextType {
  const context = useContext(U3148Context);
  if (!context) {
    throw new Error("useU3148Context must be used within a U3148ContextProvider");
  }
  return context;
}
